import { getInputFormat } from '@playcanvas/splat-transform';
import {
    Asset,
    BoundingBox,
    Entity,
    Vec3
} from 'playcanvas';

import { Element, ElementType } from './element';
import type { PCApp } from './pc-app';
import type { Scene } from './scene';

export type StreamBundleBase = {
    bucket: string;
    prefix: string;
    token: string;
    pathPrefix?: string;
};

const getOrientation = (filename: string): Vec3 => {
    try {
        const euler = (window as unknown as {
            __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphEmbedDefaultSplatEulerDeg?: number[] };
        }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphEmbedDefaultSplatEulerDeg;
        if (
            Array.isArray(euler)
            && euler.length === 3
            && euler.every((n) => typeof n === 'number' && Number.isFinite(n))
        ) {
            return new Vec3(euler[0], euler[1], euler[2]);
        }
    } catch {
        /* ignore */
    }
    switch (getInputFormat(filename)) {
        case 'spz':
            return new Vec3(0, 0, 0);
        case 'lcc':
            return new Vec3(90, 0, 180);
        default:
            return new Vec3(0, 0, 180);
    }
};

export const isVolugraphNativeStreamLodLoad = (): boolean => {
    const cfg = (window as unknown as {
        __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: {
            volugraphViewerStreamLod?: boolean;
            volugraphEditMode?: boolean;
        };
    }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
    return cfg?.volugraphViewerStreamLod === true && cfg?.volugraphEditMode !== true;
};

function readSplatBudgetFromConfig(): number | null {
    const cfg = (window as unknown as {
        __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphSplatBudget?: number };
    }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
    const n = cfg?.volugraphSplatBudget;
    if (typeof n === 'number' && Number.isFinite(n) && n >= 0) return Math.floor(n);
    return null;
}

function applySceneSplatBudget(app: PCApp): void {
    const budget = readSplatBudgetFromConfig();
    if (budget == null) return;
    const gsplatSys = app.scene?.gsplat;
    if (gsplatSys) {
        gsplatSys.splatBudget = budget;
    }
}

/** Append Firebase download tokens to relative chunk fetches for private stream bundles. */
function installTokenizedStreamFetchShim(bundleBase: StreamBundleBase): () => void {
    const origFetch = window.fetch.bind(window);
    const prefix = bundleBase.prefix.replace(/\/+$/, '');
    const token = bundleBase.token;
    const bucket = bundleBase.bucket;
    const pathPrefix = bundleBase.pathPrefix ?? '';
    const encodedPrefix = encodeURIComponent(`${prefix}/`);

    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
        let url = typeof input === 'string' ? input : input instanceof Request ? input.url : input.href;
        if (
            typeof url === 'string'
            && url.includes('firebasestorage.googleapis.com')
            && url.includes(`/b/${bucket}/`)
            && url.includes(encodedPrefix.slice(0, Math.min(encodedPrefix.length, 48)))
            && !url.includes('token=')
        ) {
            const sep = url.includes('?') ? '&' : '?';
            url = `${url}${sep}token=${encodeURIComponent(token)}`;
            if (typeof input === 'string') {
                return origFetch(url, init);
            }
            if (input instanceof Request) {
                return origFetch(new Request(url, input), init);
            }
            return origFetch(url, init);
        }
        if (
            typeof url === 'string'
            && url.includes('firebasestorage.googleapis.com')
            && !url.includes('token=')
        ) {
            try {
                const u = new URL(url);
                const pathMatch = u.pathname.match(/\/o\/(.+)$/);
                if (pathMatch) {
                    const storagePath = decodeURIComponent(pathMatch[1]);
                    if (storagePath.startsWith(`${prefix}/`) || storagePath === `${prefix}/lod-meta.json`) {
                        u.searchParams.set('token', token);
                        url = u.toString();
                        if (typeof input === 'string') {
                            return origFetch(url, init);
                        }
                        if (input instanceof Request) {
                            return origFetch(new Request(url, input), init);
                        }
                        return origFetch(url, init);
                    }
                    if (pathPrefix && storagePath.startsWith(`${prefix}/${pathPrefix}`)) {
                        u.searchParams.set('token', token);
                        url = u.toString();
                        if (typeof input === 'string') {
                            return origFetch(url, init);
                        }
                        if (input instanceof Request) {
                            return origFetch(new Request(url, input), init);
                        }
                        return origFetch(url, init);
                    }
                }
            } catch {
                /* ignore */
            }
        }
        return origFetch(input as RequestInfo, init);
    }) as typeof window.fetch;

    return () => {
        window.fetch = origFetch;
    };
}

/** View-only streamed gsplat entity — no editable per-splat state/transform channels. */
class StreamSplat extends Element {
    asset: Asset;
    entity: Entity;
    numSplats = 0;
    private localBoundStorage = new BoundingBox();
    private worldBoundStorage = new BoundingBox();
    private fetchCleanup: (() => void) | null = null;
    _visible = true;
    _name = 'stream-splat';

    constructor(asset: Asset, orientation: Vec3, fetchCleanup: (() => void) | null) {
        super(ElementType.splat);
        this.asset = asset;
        this.fetchCleanup = fetchCleanup;
        this._name = (asset.file as { filename?: string } | undefined)?.filename ?? 'stream-splat';
        this.entity = new Entity('streamSplatEntity');
        this.entity.setEulerAngles(orientation);
        this.entity.addComponent('gsplat', {
            asset,
            lodBaseDistance: 5,
            lodMultiplier: 3,
        });
        this.refreshBounds();
        const inst = this.entity.gsplat?.instance;
        if (inst?.sorter) {
            inst.sorter.on('updated', () => {
                this.refreshBounds();
            });
        }
    }

    get visible() {
        return this._visible;
    }

    set visible(value: boolean) {
        this._visible = value;
        if (this.entity.gsplat) {
            this.entity.gsplat.enabled = value;
        }
    }

    get worldTransform() {
        return this.entity.getWorldTransform();
    }

    get localBound() {
        return this.localBoundStorage;
    }

    get worldBound() {
        this.worldBoundStorage.setFromTransformedAabb(this.localBoundStorage, this.entity.getWorldTransform());
        return this.worldBoundStorage;
    }

    private refreshBounds() {
        const aabb = this.entity.gsplat?.instance?.meshInstance?.aabb;
        if (aabb) {
            this.localBoundStorage.copy(aabb);
        }
        const resource = this.asset.resource as { numSplats?: number } | null;
        if (resource && typeof resource.numSplats === 'number') {
            this.numSplats = resource.numSplats;
        }
    }

    async add() {
        this.scene.contentRoot.addChild(this.entity);
        this.entity.gsplat.layers = [this.scene.splatLayer.id];
        this.scene.boundDirty = true;
    }

    remove() {
        this.scene.contentRoot.removeChild(this.entity);
        this.scene.boundDirty = true;
    }

    destroy() {
        this.fetchCleanup?.();
        this.fetchCleanup = null;
        super.destroy();
        this.entity.destroy();
        this.asset.registry.remove(this.asset);
        this.asset.unload();
    }

    onPreRender() {
        /* Streamed gsplat uses engine materials — no SuperSplat per-splat tint/state uniforms. */
    }
}

function loadGsplatAsset(app: PCApp, url: string, filename: string): Promise<Asset> {
    return new Promise((resolve, reject) => {
        const asset = new Asset(filename, 'gsplat', { url, filename });
        app.assets.add(asset);
        asset.once('load', () => resolve(asset));
        asset.once('error', (err: string) => reject(new Error(err || `Failed to load ${filename}`)));
        app.assets.load(asset);
    });
}

export async function loadNativeStreamSplat(
    app: PCApp,
    scene: Scene,
    url: string,
    filename: string,
    bundleBase?: StreamBundleBase | null
): Promise<StreamSplat> {
    const fetchCleanup = bundleBase?.token ? installTokenizedStreamFetchShim(bundleBase) : null;
    try {
        applySceneSplatBudget(app);
        const asset = await loadGsplatAsset(app, url, filename);
        return new StreamSplat(asset, getOrientation(filename), fetchCleanup);
    } catch (e) {
        fetchCleanup?.();
        throw e;
    }
}

export { StreamSplat };
